export const API_BASE = '/api/admin';

export interface ApiErrorBody {
  message?: string;
  error?: string;
}

export class RequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
  }
}

export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as ApiErrorBody;
      message = body.message || body.error || message;
    } catch {
      message = `HTTP ${response.status}`;
    }
    throw new RequestError(response.status, message);
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Die API hat keine gültigen JSON-Daten zurückgegeben.');
  }
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof RequestError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}

export function formatDate(value: string | null, withTime = false): string {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const options: Intl.DateTimeFormatOptions = withTime
    ? {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }
    : {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      };

  return new Intl.DateTimeFormat('de-DE', options).format(date);
}

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'gerade jetzt';
  if (diffMins < 60) return `vor ${diffMins} Min.`;
  if (diffHours < 24) return `vor ${diffHours} Std.`;
  if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
  return formatDate(dateStr);
}

export interface Department {
  id: string;
  name: string;
  code: string;
  description: string | null;
  isActive: boolean;
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDepartmentInput {
  name: string;
  code: string;
  description?: string | null;
  isActive?: boolean;
}

export interface UpdateDepartmentInput {
  name?: string;
  code?: string;
  description?: string | null;
  isActive?: boolean;
}

export interface Position {
  id: string;
  name: string;
  code: string;
  description: string | null;
  isActive: boolean;
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePositionInput {
  name: string;
  code: string;
  description?: string | null;
  isActive?: boolean;
}

export interface UpdatePositionInput {
  name?: string;
  code?: string;
  description?: string | null;
  isActive?: boolean;
}

export interface OrganizationListQuery {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  sortBy?: 'name' | 'code' | 'employeeCount' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}
